import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link, navigate } from 'gatsby';
import { ButtonOutlinePrimary, ButtonPrimary, Icon, Pagination } from '@lambdacurry/component-library';
import { useMyBoxSettingsQuery, useExtraProductsQuery, useOrdersQuery } from 'lib/home/generated/queries';
import { itemIsInIndexRange } from 'lib/home/util/pagination';
import { dishContainsAllergens } from 'lib/home/meals/helpers';
import { MedusaProductVariant, OrdersQuery } from 'lib/home/generated/types/api';
import { selectExtraLineItems, selectMealLineItems } from 'lib/home/util/products';
import { Container, Grid } from 'lib/components';
import {
  ExtraCard,
  GettingStartedContent,
  GettingStartedLayout,
  GettingStartedTooMuchOnPlateFooter,
  MealCard
} from '../../components';
import { RegistrationRouteMap, RegistrationStep } from '../../store';
import { updateVariantsInMedusaCart } from '../../util/helpers';
import { Loading } from '../../components';
import { useEnsureSubscriptionStarted } from '../../hooks/queries/getting-started.queries';

const CustomizeBoxPage = () => {
  const queryClient = useQueryClient();
  const { data: extras } = useExtraProductsQuery();
  const { data: orders } = useOrdersQuery();
  const myBoxSettings = useEnsureSubscriptionStarted();

  const [extrasSelectionPage, setExtrasSelectionPage] = useState<number>(1);
  const extraItemsPerPage = 3;

  if (!extras || !orders || !myBoxSettings || !orders.me.subscription) return <Loading />;

  const extraCartLineItems = selectExtraLineItems(orders.me.subscription.current_order.medusa_cart.items);
  const mealCartLineItems = selectMealLineItems(orders.me.subscription.current_order.medusa_cart.items);
  const allExtras = extras.standardExtras;

  const unselectedExtraOptions = allExtras.filter(
    extraOption => !extraCartLineItems.find(extra => extra.variant_id === extraOption.id)
  );

  const handleProductVariantUpdate = (updatedMeal: MedusaProductVariant) =>
    queryClient.setQueryData(['Orders'], (ordersQuery: OrdersQuery) => {
      ordersQuery.me.subscription.current_order.medusa_cart = updateVariantsInMedusaCart(
        ordersQuery.me.subscription.current_order.medusa_cart,
        updatedMeal
      );

      return ordersQuery;
    });

  return (
    <GettingStartedLayout step={RegistrationStep.customizeBox}>
      <GettingStartedContent>
        <h2>Confirm Your First Box</h2>
        <p className="font-thin">
          Based on your preferences, we have hand-picked your box. You can swap any of the meals
        </p>

        <Grid className="auto-rows-fr mb-12">
          {mealCartLineItems.map((item, index) => (
            <MealCard
              actions={
                <ButtonOutlinePrimary
                  as={buttonProps => <Link {...buttonProps} to={'/getting-started/swap-meal'} state={{ item }} />}
                  data-cy={`swap-button-${index}`}
                  icon={<Icon name="swap" />}
                >
                  Swap Meal
                </ButtonOutlinePrimary>
              }
              className="select-meal-card col-span-6 md:col-span-4"
              id={`meal-card-${item.variant.id}`}
              key={index}
              meal={item.variant}
              onProductVariantUpdate={handleProductVariantUpdate}
            />
          ))}
        </Grid>

        {extraCartLineItems.length > 0 && (
          <div className="my-4">
            <h3 className="mb-4">Your Extras</h3>
            <Grid className="auto-rows-fr">
              {extraCartLineItems.map((extra, index) => (
                <ExtraCard
                  className="select-extra-card col-span-12 sm:col-span-6 lg:col-span-4"
                  extra={extra.variant}
                  quantity={extra.quantity}
                  price={extra.unit_price}
                  key={extra.id}
                  index={index}
                />
              ))}
            </Grid>
          </div>
        )}

        {!!allExtras && allExtras.length > 0 && (
          <div className="my-8">
            <div className="flex justify-between">
              <h3 className="mb-4">Popular Extras</h3>
              {unselectedExtraOptions.length > extraItemsPerPage && (
                <Pagination
                  onChange={(event, page) => setExtrasSelectionPage(page)}
                  page={extrasSelectionPage}
                  pagesCount={Math.ceil(unselectedExtraOptions.length / extraItemsPerPage)}
                />
              )}
            </div>

            <Grid className="auto-rows-fr">
              {unselectedExtraOptions
                .filter((item, index) => itemIsInIndexRange(index, extrasSelectionPage, extraItemsPerPage))
                .map((extraSelection, index) => {
                  const { meal_preferences } = myBoxSettings.me;
                  const { dietary_information } = extraSelection;
                  const alert = dishContainsAllergens(dietary_information, meal_preferences.allergens);
                  return (
                    <ExtraCard
                      className="select-extra-card col-span-12 sm:col-span-6 lg:col-span-4"
                      extra={extraSelection}
                      price={extraSelection.prices[0].amount}
                      alert={alert}
                      key={extraSelection.id}
                      index={index}
                    />
                  );
                })}
            </Grid>
          </div>
        )}
      </GettingStartedContent>

      <div className="sticky bottom-0 mt-4 md:mt-6 py-4 md:py-6 bg-white border-t border-t-brand-gray-edge">
        <Container>
          <ButtonPrimary
            className="w-full md:w-48"
            disabled={mealCartLineItems.length < 1}
            onClick={() => navigate(RegistrationRouteMap.orderSummary)}
          >
            Continue
          </ButtonPrimary>
        </Container>
      </div>

      <GettingStartedTooMuchOnPlateFooter className="!mt-0" />
    </GettingStartedLayout>
  );
};

export default CustomizeBoxPage;
